import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Card,
  CardBody,
  CardFooter,
  Typography,
  Button,
  Textarea,
  Alert,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Input,
  Radio,
} from "@material-tailwind/react";
import { toast } from "react-toastify";
import { AgentHearing, HearingDocument } from "../utils/entities/hearing";
import { useNavigate, useParams } from "react-router-dom";
import api from "../utils/axiosConfig";
import Header from "../components/Header";
import { HearingStatus, HearingAgentStatus } from "../utils/enums/hearing.enums";
import {
  FaCalendar,
  FaClock,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaBuilding,
  FaFileDownload,
} from "react-icons/fa";
import ConciliationHearingReport from "../components/ConciliationHearingReport";
import CombinedHearingReport from "../components/CombinedHearingReport";
import HearingDetailsSkeleton from "../components/skeletons/HearingDetailsSkeleton";
import LawyerFeedbackForm from "../components/LawyerFeedbackForm";
import PreMeetingReport from "../components/PreMeetingReport";
import PreMeetingReportView from "../components/PreMeetingReportView";
import { validatePixKey, isValidPhone } from "../utils/pixValidation";
import { validateEmail } from "../utils/validationMessages";
import GoogleReviewPopup, { shouldShowReviewPopup } from "../components/GoogleReviewPopup";
import PaymentScheduleAlert from "../components/PaymentScheduleAlert";

const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;
const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"];

const MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx":
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const maskLawyerPhone = (value: string): string => {
  let digits = value.replace(/\D/g, "");
  if (digits.startsWith("55") && digits.length > 11) {
    digits = digits.slice(2);
  }
  digits = digits.slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const HearingDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [hearing, setHearing] = useState<AgentHearing | null>(null);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [lawyerFeedbackData, setLawyerFeedbackData] = useState<any>(null);
  const [lawyerEmail, setLawyerEmail] = useState("");
  const [lawyerPhoneNumber, setLawyerPhoneNumber] = useState("");
  const [lawyerEmailTouched, setLawyerEmailTouched] = useState(false);
  const [lawyerPhoneTouched, setLawyerPhoneTouched] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // PIX confirmation modal states
  const [showPixModal, setShowPixModal] = useState(false);
  const [isEditingPix, setIsEditingPix] = useState(false);
  const [pixType, setPixType] = useState<string>("CPF");
  const [pixKey, setPixKey] = useState<string>("");
  const [pixError, setPixError] = useState<string>("");
  const [isUpdatingPix, setIsUpdatingPix] = useState(false);
  const [agentData, setAgentData] = useState<any>(null);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<number | null>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownloadCompanyDocument = async (doc: HearingDocument) => {
    setDownloadingDocId(doc.id);
    try {
      const response = await api.get(
        `/hearings/agent/documents/${doc.id}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Erro ao baixar documento.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  const canViewCompanyDocuments =
    hearing?.agentStatus === HearingAgentStatus.Aceito &&
    (hearing?.documents?.length ?? 0) > 0;

  useEffect(() => {
    const fetchHearing = async () => {
      try {
        const response = await api.get(`/hearings/agent/${id}`);
        setHearing(response.data);
        setLawyerEmail(response.data.lawyerEmail ?? "");
        setLawyerPhoneNumber(
          response.data.lawyerPhoneNumber
            ? maskLawyerPhone(response.data.lawyerPhoneNumber)
            : ""
        );
      } catch (error) {
        console.error("Error fetching hearing:", error);
      }
    };

    const fetchAgentData = async () => {
      try {
        const response = await api.get(`/agents/me`);
        setAgentData(response.data);
        if (response.data.pixKey) {
          setPixKey(response.data.pixKey);
        }
      } catch (error) {
        console.error("Error fetching agent data:", error);
      }
    };

    fetchHearing();
    fetchAgentData();
  }, [id]);

  // Detect mobile (small screens or coarse pointer)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    const update = () => setIsMobile(mq.matches || isCoarse);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileExtension = (fileName: string) => {
    const dotIndex = fileName.lastIndexOf(".");
    return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
  };

  const resolveUploadMimeType = (selectedFile: File): string | null => {
    if (selectedFile.type && ACCEPTED_MIME_TYPES.includes(selectedFile.type)) {
      return selectedFile.type;
    }
    const ext = getFileExtension(selectedFile.name);
    return MIME_BY_EXTENSION[ext] || null;
  };

  const validateSelectedFile = (selectedFile: File): boolean => {
    const extension = getFileExtension(selectedFile.name);
    const validExtension = ACCEPTED_EXTENSIONS.includes(extension);
    const resolvedMime = resolveUploadMimeType(selectedFile);

    if (!validExtension || !resolvedMime) {
      toast.error("Tipo de arquivo inválido. Envie PDF, DOC ou DOCX.");
      return false;
    }

    if (selectedFile.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error("Arquivo excede o limite de 100MB.");
      return false;
    }

    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const selectedFile = e.target.files[0];
      if (!validateSelectedFile(selectedFile)) {
        setFile(null);
        resetFileInput();
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const selectedFile = e.dataTransfer.files[0];
      if (!validateSelectedFile(selectedFile)) {
        setFile(null);
        e.dataTransfer.clearData();
        return;
      }
      setFile(selectedFile);
      e.dataTransfer.clearData();
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFinishHearingClick = () => {
    // Skip PIX modal for Pre-Meeting (type 6)
    if (hearing?.hearingType?.id === 6) {
      proceedWithFinishHearing(false, undefined);
      return;
    }
    // Open PIX confirmation modal before finishing
    setShowPixModal(true);
  };

  const handlePixConfirmation = (isCorrect: boolean) => {
    if (!isCorrect) {
      setIsEditingPix(true);
    } else {
      // PIX is correct, proceed with finishing
      proceedWithFinishHearing(true, "PIX confirmado como correto pelo preposto");
    }
  };

  const handlePixTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPixType(e.target.value);
    setPixError("");
  };

  const handlePixKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    if (pixType === "Telefone") {
      // Remove formatação anterior
      value = value.replace(/\D/g, "");
      // Adiciona +55 se necessário
      if (!value.startsWith("55")) {
        value = "55" + value;
      }
      // Limita a 13 dígitos (55 + 11 dígitos do telefone)
      if (value.length > 13) {
        value = value.slice(0, 13);
      }
    } else if (pixType === "CPF") {
      value = value.replace(/\D/g, "");
      if (value.length > 11) {
        value = value.slice(0, 11);
      }
    }
    
    // E-mail: sem formatação especial
    setPixKey(value);
    setPixError("");
  };

  const handleSavePixKey = async () => {
    // Validate PIX key
    const validation = validatePixKey(pixKey, pixType);
    if (!validation.isValid) {
      setPixError(validation.error || "Chave PIX inválida");
      return;
    }

    setIsUpdatingPix(true);
    try {
      const cleanedPixKey = pixKey && /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(pixKey.trim())
        ? pixKey.replace(/\D/g, '')
        : pixKey;
      await api.put(`/agents/me`, { pixKey: cleanedPixKey });
      toast.success("Chave PIX atualizada com sucesso!");
      
      // Create observation about the edit
      const oldPixKey = agentData?.pixKey || "Nenhuma chave cadastrada";
      const observation = `PIX corrigido pelo preposto. Chave anterior: ${oldPixKey}. Nova chave (${pixType}): ${pixKey}`;
      
      setAgentData({ ...agentData, pixKey });
      setIsEditingPix(false);
      // Proceed with finishing after successful update - isPixCorrect=true because it was corrected
      proceedWithFinishHearing(true, observation);
    } catch (error) {
      console.error("Error updating PIX key:", error);
      toast.error("Erro ao atualizar chave PIX. Tente novamente.");
    } finally {
      setIsUpdatingPix(false);
    }
  };

  const uploadReportFileToS3 = async (selectedFile: File): Promise<string> => {
    const mimeType = resolveUploadMimeType(selectedFile);
    if (!mimeType) {
      throw new Error("INVALID_FILE_TYPE");
    }

    const initResponse = await api.post(`/hearings/agent/upload-url/${id}`, {
      fileName: selectedFile.name,
      mimeType,
      size: selectedFile.size,
    });

    const { uploadUrl, objectKey } = initResponse.data;
    await axios.put(uploadUrl, selectedFile, {
      headers: {
        "Content-Type": mimeType,
      },
      timeout: 5 * 60 * 1000,
    });

    return objectKey;
  };

  const proceedWithFinishHearing = async (isPixCorrect: boolean, pixObservation?: string) => {
    setShowPixModal(false);
    setIsLoading(true);
    let combinedReportData: Record<string, unknown>;

    if (
      hearing?.hearingType?.id &&
      [1, 3, 6].includes(hearing.hearingType.id) &&
      reportData?.formattedData
    ) {
      combinedReportData = {
        ...reportData.formattedData,
        ...(lawyerFeedbackData?.formattedData || {}),
        isPixCorrect,
        pixObservation: pixObservation || undefined,
      };
    } else {
      combinedReportData = {
        notes,
        ...(lawyerFeedbackData?.formattedData || {}),
        isPixCorrect,
        pixObservation: pixObservation || undefined,
      };
    }

    try {
      let fileObjectKey: string | undefined;
      if (file) {
        fileObjectKey = await uploadReportFileToS3(file);
      }

      await api.post(`/hearings/agent/finish/${id}`, {
        reportData: combinedReportData,
        fileObjectKey,
        lawyerEmail: lawyerEmail.trim(),
        lawyerPhoneNumber: lawyerPhoneNumber.trim(),
      });
      toast.success("Audiência finalizada com sucesso!");
      
      // Verificar se deve mostrar popup de avaliação Google
      if (shouldShowReviewPopup()) {
        setShowReviewPopup(true);
      } else {
        navigate("/");
      }
    } catch (error: any) {
      console.error("Error finishing hearing:", error);
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message;

      if (status === 413) {
        toast.error("Arquivo excede o limite de 100MB.");
      } else if (status === 400) {
        toast.error(Array.isArray(apiMessage) ? apiMessage[0] : apiMessage || "Arquivo inválido para upload.");
      } else if (status === 403) {
        toast.error("Link de upload expirado. Tente enviar novamente.");
      } else if (error?.code === "ECONNABORTED") {
        toast.error("Upload demorou mais que o esperado. Verifique sua conexão e tente novamente.");
      } else {
        toast.error("Ocorreu um erro ao finalizar a audiência.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportDataChange = (data: any) => {
    setReportData(data);
  };

  const isHearingDateTimeInTheFuture = () => {
    if (!hearing?.hearingDate || !hearing?.hearingTime) return true;
    const hearingDateTime = new Date(`${hearing.hearingDate}T${hearing.hearingTime}`);
    return hearingDateTime > new Date();
  };

  const formatDate = (date: string) => {
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const getReportComponent = () => {
    if (!hearing) return null;

    switch (hearing.hearingType?.id) {
      case 1:
      case 4:
        return (
          <ConciliationHearingReport
            onChange={handleReportDataChange}
            includeLawyerFeedback={false}
          />
        );
      case 2:
      case 3:
        return (
          <CombinedHearingReport
            onChange={handleReportDataChange}
            includeLawyerFeedback={false}
          />
        );
      case 6:
        return (
          <PreMeetingReport
            onChange={handleReportDataChange}
          />
        );
      default:
        return (
          <Textarea
            label="Relatório"
            value={
              [HearingStatus.Agendada, HearingStatus.Confirmada].includes(
                hearing.status
              )
                ? notes
                : hearing.notes
            }
            disabled={[
              HearingStatus.Finalizada,
              HearingStatus.Cancelada,
            ].includes(hearing.status)}
            onChange={(e) => setNotes(e.target.value)}
            className="mb-4"
          />
        );
    }
  };

  const isLawyerEmailValid = validateEmail(lawyerEmail).isValid;
  const isLawyerPhoneValid =
    lawyerPhoneNumber.trim() === "" || isValidPhone(lawyerPhoneNumber);

  const isFinishButtonDisabled = () => {
    if (!hearing) return true;
        if (isHearingDateTimeInTheFuture()) {
      return true;
    }

    if (!isLawyerEmailValid || !isLawyerPhoneValid) {
      return true;
    }

    if ([1, 2, 3, 4, 6].includes(hearing.hearingType?.id)) {
      const reportsOk = !!reportData && !!reportData.isValid;
      // Only require lawyer feedback if company needs it
      const lawyerOk = hearing.company?.needsLawyerFeedback 
        ? (!!lawyerFeedbackData && !!lawyerFeedbackData.isValid)
        : true;
      return !(reportsOk && lawyerOk);
    }

    return !notes;
  };

  if (!hearing) {
    return <HearingDetailsSkeleton />;
  }

  const isPreMeeting = hearing?.hearingType?.id === 6;

  const cannotSubmitReport = [
    HearingStatus.Finalizada,
    HearingStatus.Cancelada,
  ].includes(hearing.status);

  const DetailField = ({
    label,
    value,
    icon,
  }: {
    label: string;
    value: string | JSX.Element;
    icon?: React.ReactNode;
  }) => {
    const isEmpty =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim().length === 0);
    const displayValue = isEmpty ? (
      <span className="text-gray-400">Não informado</span>
    ) : (
      value
    );

    return (
      <div className="flex flex-col mb-0.5 md:mb-1">
        <div className="text-[11px] md:text-sm text-preppoBlue font-normal md:font-medium mb-0.5 flex items-center">
          {icon && <span className="hidden md:inline-flex mr-2 text-preppoBlue">{icon}</span>}
          {label}
        </div>
        <div className="text-sm md:text-[15px] text-gray-700 leading-snug md:leading-normal font-medium">{displayValue}</div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="flex flex-col space-y-2 md:space-y-3 p-3 md:p-6 justify-center items-center">
        {hearing.status === HearingStatus.Finalizada && (
          <div className="max-w-3xl w-full flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-[13px] md:text-sm leading-snug text-emerald-800">
            <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-emerald-400 mt-1.5"></span>
            <p>Essa audiência já foi finalizada.</p>
          </div>
        )}
        {hearing.status === HearingStatus.Realizada && (
          <PaymentScheduleAlert className="max-w-3xl w-full" />
        )}
        <Card className="bg-white shadow-sm border border-slate-100 rounded-xl max-w-3xl w-full overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-blue-400"></span>
              <Typography
                variant="h6"
                className="text-slate-600 tracking-tight text-sm md:text-base font-semibold truncate"
              >
                {isPreMeeting ? "Detalhes da reunião prévia" : "Detalhes da audiência"}
              </Typography>
            </div>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-medium uppercase tracking-wide shrink-0 ${
                hearing.status === HearingStatus.Agendada
                  ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                  : hearing.status === HearingStatus.Confirmada
                  ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
                  : hearing.status === HearingStatus.Realizada
                  ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
                  : hearing.status === HearingStatus.Finalizada
                  ? "bg-green-50 text-green-700 ring-1 ring-green-200"
                  : hearing.status === HearingStatus.Cancelada
                  ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                  : "bg-gray-100 text-gray-700 ring-1 ring-gray-200"
              }`}
            >
              {String(hearing.status)}
            </span>
          </div>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-5 gap-y-0.5 md:gap-y-1">
              <DetailField
                label="Data"
                value={formatDate(hearing.hearingDate)}
                icon={<FaCalendar />}
              />

              <DetailField
                label="Horário"
                value={hearing.timezoneInfo 
                  ? `${hearing.hearingTime.substring(0, 5)} (${hearing.timezoneInfo.abbreviation})`
                  : hearing.hearingTime.substring(0, 5)}
                icon={<FaClock />}
              />

              <DetailField
                label="Tipo"
                value={hearing.hearingType.description}
              />

              <DetailField
                label="Modalidade"
                value={hearing.modality === "1" ? "Presencial" : "Virtual"}
              />

              <DetailField
                label="Estado"
                value={hearing.state?.slug || "N/A"}
              />

              <DetailField
                label="Distrito"
                value={hearing.district?.name || "N/A"}
              />

              <DetailField label="Vara" value={hearing.court} />

              <DetailField label="Reclamadas" value={hearing.defendants} />

              <DetailField label="Reclamantes" value={hearing.plaintiff} />

              <DetailField
                label="Nome do advogado(a)"
                value={hearing.lawyerName}
                icon={<FaUser />}
              />

              <DetailField
                label="Telefone do advogado(a)"
                value={hearing.lawyerPhoneNumber}
                icon={<FaPhone />}
              />

              <DetailField
                label="Escritório de advocacia"
                value={hearing.lawFirm}
                icon={<FaBuilding />}
              />

              <div className="col-span-2">
                <DetailField
                  label={isPreMeeting ? "Link da reunião" : "Link da audiência"}
                  value={
                    hearing.virtualHearingLink ? (
                      <a
                        href={hearing.virtualHearingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-preppoBlue underline"
                      >
                        {hearing.virtualHearingLink}
                      </a>
                    ) : (
                      ""
                    )
                  }
                />
              </div>

              <div className="col-span-2">
                <DetailField label="Observações" value={hearing.notes ?? ""} />
              </div>
            </div>
          </CardBody>
        </Card>

        {hearing.instructions?.replace(/<[^>]*>/g, '').trim() && (
          <Card className="bg-white shadow-sm border border-slate-100 rounded-xl max-w-3xl w-full overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
              <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-indigo-400"></span>
              <Typography
                variant="h6"
                className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
              >
                Instruções
              </Typography>
            </div>
            <CardBody>
              <div dangerouslySetInnerHTML={{ __html: hearing.instructions }} />
            </CardBody>
          </Card>
        )}

        {canViewCompanyDocuments && (
          <Card className="bg-white shadow-sm border border-slate-100 rounded-xl max-w-3xl w-full overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
              <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-cyan-400"></span>
              <Typography
                variant="h6"
                className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
              >
                Documentos da empresa
              </Typography>
            </div>
            <CardBody>
              <Typography
                variant="small"
                className="mb-3 text-gray-600 text-[13px] md:text-sm"
              >
                Documentação disponibilizada pela empresa contratante para esta audiência.
              </Typography>
              <div className="space-y-2">
                {hearing.documents!.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between gap-2 p-2 md:p-3 border border-slate-200 rounded-lg"
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] md:text-sm font-medium text-gray-800 truncate">
                        {doc.fileName}
                      </p>
                      <p className="text-[11px] md:text-xs text-gray-500">
                        {formatFileSize(doc.sizeBytes)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outlined"
                      className="flex items-center gap-1 shrink-0"
                      disabled={downloadingDocId === doc.id}
                      onClick={() => handleDownloadCompanyDocument(doc)}
                    >
                      <FaFileDownload className="text-sm" />
                      Baixar
                    </Button>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Show Pre-Meeting Report if finalized */}
        {hearing.status === HearingStatus.Finalizada &&
          isPreMeeting &&
          hearing.report && (
          <PreMeetingReportView report={hearing.report} />
        )}

        {!isHearingDateTimeInTheFuture() && !cannotSubmitReport && (
          <>
            {/* Lawyer Feedback Box - Only show if company needs lawyer feedback */}
            {hearing.company?.needsLawyerFeedback && (
              <Card className="bg-white shadow-sm border border-slate-100 rounded-xl max-w-3xl w-full overflow-hidden">
                <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
                  <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-purple-400"></span>
                  <Typography
                    variant="h6"
                    className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
                  >
                    Avaliação do advogado
                  </Typography>
                </div>
                <CardBody>
                  <div className="mb-1 md:mb-2 text-[13px] md:text-base [&_label]:mb-0.5 md:[&_label]:mb-1 [&_fieldset]:space-y-1 md:[&_fieldset]:space-y-2 [&_p]:my-0.5 md:[&_p]:my-1">
                    <LawyerFeedbackForm onChange={setLawyerFeedbackData} />
                  </div>
                </CardBody>
              </Card>
            )}

            <Card className="bg-white shadow-sm border border-slate-100 rounded-xl max-w-3xl w-full overflow-hidden">
              <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
                <span aria-hidden className="h-2 w-2 rounded-full shrink-0 bg-emerald-400"></span>
                <Typography
                  variant="h6"
                  className="text-slate-600 tracking-tight text-sm md:text-base font-semibold"
                >
                  {isPreMeeting ? "Finalizar reunião" : "Finalizar audiência"}
                </Typography>
              </div>
              <CardBody className="p-3 md:p-6">
                <Typography variant="small" className="mb-4 md:mb-5 text-gray-700 text-[13px] md:text-base leading-snug md:leading-normal">
                  {isPreMeeting 
                    ? "Envie o relatório para finalizar. Após o envio, o relatório e os arquivos ficam disponíveis para a Preppo e para a empresa contratante e não poderão ser alterados."
                    : "Envie o relatório para finalizar e agendar o pagamento. Após o envio, o relatório e os arquivos ficam disponíveis para a Preppo e para a empresa contratante e não poderão ser alterados."
                  }
                </Typography>

                <div className="mb-4 md:mb-5 space-y-3">
                  <Typography variant="small" className="text-gray-700 text-[13px] md:text-sm leading-snug">
                    Informe o e-mail do advogado que compareceu à audiência. O telefone é opcional.
                  </Typography>
                  <div>
                    <Input
                      type="email"
                      label="E-mail do advogado"
                      value={lawyerEmail}
                      onChange={(e) => setLawyerEmail(e.target.value)}
                      onBlur={() => setLawyerEmailTouched(true)}
                      crossOrigin={undefined}
                      icon={<FaEnvelope className="h-4 w-4" />}
                    />
                    {lawyerEmailTouched && !isLawyerEmailValid && (
                      <Typography className="text-red-500 text-xs mt-1">
                        {validateEmail(lawyerEmail).message || "Informe um e-mail válido"}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Input
                      type="tel"
                      label="Telefone do advogado"
                      placeholder="(00) 00000-0000"
                      value={lawyerPhoneNumber}
                      onChange={(e) => setLawyerPhoneNumber(maskLawyerPhone(e.target.value))}
                      onBlur={() => setLawyerPhoneTouched(true)}
                      crossOrigin={undefined}
                      icon={<FaPhone className="h-4 w-4" />}
                    />
                    {lawyerPhoneTouched && !isLawyerPhoneValid && (
                      <Typography className="text-red-500 text-xs mt-1">
                        Formato de telefone inválido. Use: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
                      </Typography>
                    )}
                  </div>
                </div>

                <div className="mb-1 md:mb-2 text-[13px] md:text-base [&_label]:mb-0.5 md:[&_label]:mb-1 [&_fieldset]:space-y-1 md:[&_fieldset]:space-y-2 [&_p]:my-0.5 md:[&_p]:my-1 [&_.form-group]:my-0.5 md:[&_.form-group]:my-1">
                  {getReportComponent()}
                </div>

                {!isPreMeeting && (
                  <div className="mb-1 md:mb-2">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      {...(!isMobile && { onDrop: handleDrop, onDragOver: handleDragOver })}
                      className={`border-2 border-dashed rounded-lg p-2 md:p-3 text-center cursor-pointer transition-colors ${
                        cannotSubmitReport
                          ? "opacity-60 cursor-not-allowed"
                          : "hover:border-preppoBlue/60"
                      }`}
                      aria-disabled={cannotSubmitReport}
                    >
                      <div className="text-[13px] md:text-base text-gray-700">
                        {file ? (
                          <span>
                            Arquivo selecionado: <strong>{file.name}</strong>
                          </span>
                        ) : (
                          <span>
                            {isMobile ? (
                              <>Toque para selecionar o arquivo do relatório</>
                            ) : (
                              <>Arraste e solte o arquivo do relatório aqui ou <span className="text-preppoBlue underline">clique para selecionar</span></>
                            )}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] md:text-xs text-gray-500 mt-0 md:mt-1">Formatos aceitos: PDF, DOC, DOCX. Tamanho máx. 100MB.</div>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_EXTENSIONS.join(",")}
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={cannotSubmitReport}
                    />
                  </div>
                )}
              </CardBody>
              {!cannotSubmitReport && (
                <CardFooter className="sticky bottom-0 bg-white pt-1 md:pt-2">
                  <Button
                    disabled={isLoading || isFinishButtonDisabled()}
                    className="bg-green-600 hover:bg-green-700 w-full text-xs md:text-base py-2 md:py-3"
                    onClick={handleFinishHearingClick}
                  >
                    {isLoading ? `Enviando...` : `Finalizar`}
                  </Button>
                  {isHearingDateTimeInTheFuture() && (
                    <Typography
                      variant="small"
                      className="text-red-500 text-center mt-2 text-xs md:text-sm"
                    >
                      A finalização da audiência ficará disponível a partir do dia
                      da audiência.
                    </Typography>
                  )}
                  {!isHearingDateTimeInTheFuture() && isFinishButtonDisabled() && (
                    <Typography
                      variant="small"
                      className="text-gray-700 text-center mt-2 text-xs md:text-sm"
                    >
                      Para habilitar o botão, preencha todos os campos obrigatórios.
                    </Typography>
                  )}
                </CardFooter>
              )}
            </Card>
          </>
        )}
      </div>

      {/* PIX Confirmation Modal */}
      <Dialog open={showPixModal} handler={() => {}} size="sm">
        <DialogHeader className="text-preppoBlue">
          Confirmar Chave PIX
        </DialogHeader>
        <DialogBody className="space-y-4">
          {!isEditingPix ? (
            <>
              <Typography className="text-gray-700">
                Sua chave PIX cadastrada é:
              </Typography>
              <div className="bg-blue-50 p-4 rounded-lg">
                <Typography className="font-semibold text-preppoBlue text-lg">
                  {agentData?.pixKey || "Nenhuma chave cadastrada"}
                </Typography>
              </div>
              <Typography className="text-gray-700 mt-4">
                A chave PIX está correta?
              </Typography>
              <div className="flex gap-3 mt-2">
                <Button
                  color="green"
                  onClick={() => handlePixConfirmation(true)}
                  className="flex-1"
                >
                  PIX está correto
                </Button>
                <Button
                  color="red"
                  onClick={() => handlePixConfirmation(false)}
                  className="flex-1"
                >
                  PIX está incorreto
                </Button>
              </div>
            </>
          ) : (
            <>
              <Typography className="text-gray-700 mb-4">
                Atualize sua chave PIX:
              </Typography>
              
              <div className="space-y-4">
                <div>
                  <Typography variant="small" className="mb-2 font-semibold text-gray-700">
                    Tipo de Chave PIX
                  </Typography>
                  <div className="flex gap-2">
                    <Radio
                      name="pixType"
                      label="CPF"
                      value="CPF"
                      checked={pixType === "CPF"}
                      onChange={handlePixTypeChange}
                      crossOrigin={undefined}
                    />
                    <Radio
                      name="pixType"
                      label="Telefone"
                      value="Telefone"
                      checked={pixType === "Telefone"}
                      onChange={handlePixTypeChange}
                      crossOrigin={undefined}
                    />
                    <Radio
                      name="pixType"
                      label="E-mail"
                      value="E-mail"
                      checked={pixType === "E-mail"}
                      onChange={handlePixTypeChange}
                      crossOrigin={undefined}
                    />
                  </div>
                </div>

                <Alert color="amber" className="flex items-start gap-2">
                  <span className="text-lg">⚠️</span>
                  <Typography variant="small" className="font-normal">
                    <strong>ATENÇÃO:</strong> Não é permitido cadastrar chave PIX de terceiros ou de pessoas jurídicas. A chave PIX deve estar em seu nome (CPF).
                  </Typography>
                </Alert>

                <div>
                  <Input
                    label="Chave PIX"
                    value={pixKey}
                    onChange={handlePixKeyChange}
                    error={!!pixError}
                    crossOrigin={undefined}
                  />
                  {pixError && (
                    <Typography variant="small" color="red" className="mt-1">
                      {pixError}
                    </Typography>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter className="space-x-2">
          {isEditingPix ? (
            <>
              <Button
                variant="text"
                color="gray"
                onClick={() => {
                  setShowPixModal(false);
                  setIsEditingPix(false);
                  setPixError("");
                }}
                disabled={isUpdatingPix}
              >
                Cancelar
              </Button>
              <Button
                color="green"
                onClick={handleSavePixKey}
                disabled={isUpdatingPix || !pixKey}
              >
                {isUpdatingPix ? "Salvando..." : "Salvar e Finalizar"}
              </Button>
            </>
          ) : (
            <Button
              variant="text"
              color="gray"
              onClick={() => {
                setShowPixModal(false);
              }}
            >
              Cancelar
            </Button>
          )}
        </DialogFooter>
      </Dialog>

      {/* Google Review Popup */}
      <GoogleReviewPopup 
        show={showReviewPopup} 
        onClose={() => {
          setShowReviewPopup(false);
          navigate("/");
        }} 
      />
    </>
  );
};

export default HearingDetailsPage;
